import { STRUCTURE_GRID_SIZE } from 'data/constants';
import {
  GridObjectType,
  GridSpaceDataType,
  LineSetType,
  PositionAndDimensionsType
} from 'data/types';

export type Obstacle = { x: number; y: number; width: number; height: number };

export const EQUIPMENT_RENDERED_TILES = 3;
export const LINESET_POINT_RADIUS = 15;
export const LINESET_SEGMENT_HALF_WIDTH = 8;

function pointMatchesLevel(
  levelUniqueId: string | undefined,
  pointLevelUniqueId?: string | null
): boolean {
  if (!levelUniqueId) return true;
  if (!pointLevelUniqueId) return true;
  return pointLevelUniqueId === levelUniqueId;
}

export function getObstaclesFromSpaces(
  spaces: GridSpaceDataType[]
): Obstacle[] {
  return spaces.map((space) => ({
    x: space.position.x,
    y: space.position.y,
    width: space.position.width,
    height: space.position.height
  }));
}

export function getObstaclesFromObjects(objects: GridObjectType[]): Obstacle[] {
  const size = EQUIPMENT_RENDERED_TILES * STRUCTURE_GRID_SIZE;
  return objects.map((obj) => ({
    x: obj.position.x * STRUCTURE_GRID_SIZE,
    y: obj.position.y * STRUCTURE_GRID_SIZE,
    width: size,
    height: size
  }));
}

export function segmentToObstacle(
  start: { x: number; y: number },
  end: { x: number; y: number },
  halfWidth: number = LINESET_SEGMENT_HALF_WIDTH
): Obstacle {
  const minX = Math.min(start.x, end.x) - halfWidth;
  const minY = Math.min(start.y, end.y) - halfWidth;
  return {
    x: minX,
    y: minY,
    width: Math.abs(end.x - start.x) + halfWidth * 2,
    height: Math.abs(end.y - start.y) + halfWidth * 2
  };
}

export function getObstaclesFromLineSets(
  lineSets: LineSetType[],
  levelUniqueId?: string
): Obstacle[] {
  const obstacles: Obstacle[] = [];

  for (const lineSet of lineSets) {
    const { points } = lineSet;
    for (let i = 0; i < points.length; i += 1) {
      if (pointMatchesLevel(levelUniqueId, points[i].levelUniqueId)) {
        const x = points[i].positionX * STRUCTURE_GRID_SIZE;
        const y = points[i].positionY * STRUCTURE_GRID_SIZE;
        obstacles.push({
          x: x - LINESET_POINT_RADIUS,
          y: y - LINESET_POINT_RADIUS,
          width: LINESET_POINT_RADIUS * 2,
          height: LINESET_POINT_RADIUS * 2
        });
      }

      if (
        i < points.length - 1 &&
        pointMatchesLevel(levelUniqueId, points[i].levelUniqueId) &&
        pointMatchesLevel(levelUniqueId, points[i + 1].levelUniqueId)
      ) {
        obstacles.push(
          segmentToObstacle(
            {
              x: points[i].positionX * STRUCTURE_GRID_SIZE,
              y: points[i].positionY * STRUCTURE_GRID_SIZE
            },
            {
              x: points[i + 1].positionX * STRUCTURE_GRID_SIZE,
              y: points[i + 1].positionY * STRUCTURE_GRID_SIZE
            }
          )
        );
      }
    }
  }

  return obstacles;
}

export function filterObstaclesOutsideZoneSpaces(
  obstacles: Obstacle[],
  zoneSpacePositions: PositionAndDimensionsType[]
): Obstacle[] {
  return obstacles.filter((obs) => {
    const obsRight = obs.x + obs.width;
    const obsBottom = obs.y + obs.height;
    return !zoneSpacePositions.some(
      (sp) =>
        obs.x >= sp.x &&
        obsRight <= sp.x + sp.width &&
        obs.y >= sp.y &&
        obsBottom <= sp.y + sp.height
    );
  });
}

export function buildZoneLabelObstacles(params: {
  spaces: GridSpaceDataType[];
  objects: GridObjectType[];
  lineSets: LineSetType[];
  zoneSpacePositions: PositionAndDimensionsType[];
  additionalObstacles?: Obstacle[];
  levelUniqueId?: string;
}): Obstacle[] {
  const equipmentAndLines = filterObstaclesOutsideZoneSpaces(
    [
      ...getObstaclesFromObjects(params.objects),
      ...getObstaclesFromLineSets(params.lineSets, params.levelUniqueId)
    ],
    params.zoneSpacePositions
  );
  return [
    ...getObstaclesFromSpaces(params.spaces),
    ...equipmentAndLines,
    ...(params.additionalObstacles ?? [])
  ];
}
